import { baseApi } from "./baseApi";

const BRANCH_BASE_URL = "/branches";

const generateBranchTags = (result) =>
  result?.data?.map(({ _id }) => ({ type: "Branch", id: _id })) ?? [
    { type: "Branch", id: "LIST" },
  ];

export const branchApi = baseApi.injectEndpoints({
  tagTypes: ["Branch"],

  endpoints: (builder) => ({
    getBranchList: builder.query({
      query: (params) => ({
        url: `${BRANCH_BASE_URL}/branch-list`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "Branch",
                id: _id,
              })),
              { type: "Branch", id: "LIST" },
            ]
          : [{ type: "Branch", id: "LIST" }],
    }),

    getActiveBranchList: builder.query({
      query: (params) => ({
        url: `${BRANCH_BASE_URL}/active`,
        params,
      }),
      providesTags: generateBranchTags,
    }),
    getDDLBranchList: builder.query({
      query: (params) => ({ url: `${BRANCH_BASE_URL}/ddl`, params }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result?.data?.map(({ _id }) => ({ type: "Branch", _id })),
              { type: "Branch", id: "DDL" },
            ]
          : [{ type: "Branch", id: "DDL" }],
    }),

    getDDLDestinationBranchList: builder.query({
      query: (params) => ({
        url: `${BRANCH_BASE_URL}/ddl-destination-branch`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result?.data?.map(({ _id }) => ({ type: "Branch", _id })),
              { type: "Branch", id: "DDL_DESTINATION" },
            ]
          : [{ type: "Branch", id: "DDL_DESTINATION" }],
    }),

    getBranchByRegionDDL: builder.query({
      query: ({ regionId, account, entity }) => ({
        url: `${BRANCH_BASE_URL}/region/${regionId || "all"}`,
        method: "GET",
        params: { account, entity },
      }),
      providesTags: (result, error, { regionId }) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "Branch", id: _id })),
              { type: "Branch", id: regionId ? `Region-${regionId}` : "DDL" },
            ]
          : [{ type: "Branch", id: regionId ? `Region-${regionId}` : "DDL" }],
    }),

    getBranchDetail: builder.query({
      query: (id) => `${BRANCH_BASE_URL}/branch-edit/${id}`,
      providesTags: (result, error, id) => [
        { type: "Branch", id },
        { type: "Branch", id: "LIST" },
      ],
    }),
    getBranchByDistrictId: builder.query({
      query: (districtId) => ({
        url: `${BRANCH_BASE_URL}/district/${districtId}`,
        method: "GET",
      }),
      providesTags: (result, error, districtId) => [
        { type: "Branch", id: `District-${districtId}` },
        { type: "Branch", id: "LIST" },
      ],
    }),
    getBranchByMunicpalityId: builder.query({
      query: (municpalityId) => ({
        url: `${BRANCH_BASE_URL}/municpality/${municpalityId}`,
        method: "GET",
      }),
      providesTags: (result, error, municpalityId) => [
        { type: "Branch", id: `Municpality-${municpalityId}` },
        { type: "Branch", id: "LIST" },
      ],
    }),
    createBranch: builder.mutation({
      query: (payload) => ({
        url: `${BRANCH_BASE_URL}/branch-create`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Branch", id: "LIST" }],
    }),
    createBranchServices: builder.mutation({
      query: (payload) => ({
        url: `${BRANCH_BASE_URL}/services`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Branch", id: "LIST" }],
    }),

    updateBranchNumber: builder.mutation({
      query: ({ branchId, numberId, ...body }) => ({
        url: `${BRANCH_BASE_URL}/${branchId}/number/${numberId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "Branch", id: "LIST" }],
    }),

    updateBranchNumberStatus: builder.mutation({
      query: ({ branchId, numberId, isActive }) => ({
        url: `${BRANCH_BASE_URL}/${branchId}/number/${numberId}/status`,
        method: "PUT",
        body: { isActive },
      }),
      invalidatesTags: [{ type: "Branch", id: "LIST" }],
    }),

    updateBranch: builder.mutation({
      query: ({ _id, ...body }) => ({
        url: `${BRANCH_BASE_URL}/branch-edit/${_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { _id }) => [
        { type: "Branch", id: _id },
        { type: "Branch", id: "LIST" },
      ],
    }),

    removeBranchService: builder.mutation({
      query: ({ branchId, serviceId }) => ({
        url: `${BRANCH_BASE_URL}/${branchId}/service/${serviceId}/remove`,
        method: "PUT",
      }),
      invalidatesTags: [{ type: "Branch", id: "LIST" }],
    }),

    searchBranch: builder.query({
      query: (params) => ({ url: `${BRANCH_BASE_URL}/search`, params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: "Branch", id: _id })),
              { type: "Branch", id: "SEARCH" },
            ]
          : [{ type: "Branch", id: "SEARCH" }],
    }),

    getBranchesByScope: builder.query({
      query: (params) => ({ url: `${BRANCH_BASE_URL}/scope`, params }),
      providesTags: (result, error, params) => [
        {
          type: "Branch",
          id: `Scope-${params?.assignmentType}-${params?.regionIds || ""}-${params?.districtIds || ""}-${params?.municipalityIds || ""}`,
        },
      ],
    }),
  }),
});

export const {
  useGetBranchListQuery,
  useLazyGetBranchListQuery,
  useLazyGetActiveBranchListQuery,
  useLazyGetDDLBranchListQuery,
  useLazyGetDDLDestinationBranchListQuery,
  useGetBranchByRegionDDLQuery,
  useLazyGetBranchByRegionDDLQuery,
  useGetBranchDetailQuery,
  useGetBranchByDistrictIdQuery,
  useLazyGetBranchByDistrictIdQuery,
  useGetBranchByMunicpalityIdQuery,
  useLazyGetBranchByMunicpalityIdQuery,
  useCreateBranchMutation,
  useCreateBranchServicesMutation,
  useUpdateBranchNumberMutation,
  useUpdateBranchNumberStatusMutation,
  useUpdateBranchMutation,
  useRemoveBranchServiceMutation,
  useLazySearchBranchQuery,
  useLazyGetBranchesByScopeQuery,
} = branchApi;
